/**
 * First legal pickup the guest may be offered.
 *
 * pending_payment holds and changeover windows already decide WHICH days
 * are free. This file decides WHICH CLOCK on those days is still in the
 * future in the host timezone. Without it the calendar offers "today"
 * and the time strip offers 10:00 / 00:00 after that hour has passed —
 * checkout looks fine, createBooking dies with "start time cannot be
 * in the past", and we never open Wompi.
 *
 * AND WHICH CLOCK CLEARS THE HOST-RESPONSE LEAD. "Still in the future" is not
 * enough: checkout and `createBooking` refuse any start inside
 * `BOOKING_MIN_LEAD_MS` (the host's two-hour decision window plus a buffer) and
 * answer "elegí una salida al menos 3 horas después de ahora". Offering a slot
 * 30 minutes out is the same defect one step later — the guest picks dates,
 * reaches the card form, and the button that should charge them is dead. The
 * lead is not restated here: it comes from `startsTooSoonForHostApproval`, the
 * same predicate the checkout and the booking service consult, so the offer and
 * the refusal cannot drift apart again.
 */
import { bookingClockNow, toBookingDateTimeISO } from './dates'
import { startsTooSoonForHostApproval } from './host-approval-policy'
import { firstSlotAfter, firstSlotOnOrAfter, TIME_SLOTS } from './time-slots'

export function laterHHmm(a?: string, b?: string): string | undefined {
  if (!a) return b
  if (!b) return a
  return a > b ? a : b
}

export function addBookingDaysISO(iso: string, days: number): string {
  const date = new Date(`${iso}T12:00:00`)
  date.setDate(date.getDate() + days)
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * Earliest half-hour the guest can pick on `date`.
 *
 * Past civil days: none.
 * Today: the next slot after the host clock once the current minute has
 * started, then the later of that and the changeover floor (`free_from`), and
 * then forward again until the host-response lead clears.
 * Future days: midnight, or the changeover floor if that day is a handoff —
 * also advanced past the lead, because the lead is measured against the real
 * clock, not against the calendar day.
 */
export function earliestPickupTimeOnDate(params: {
  date: string
  now?: Date
  changeoverMin?: string
}): string | null {
  // Use one instant for the zoned civil clock and the second-level tie-break.
  // `bookingClockNow` intentionally returns HH:MM for display, but a booking
  // created at 09:30:01 cannot start at 09:30:00. Without this distinction the
  // default was valid for one render and then dead before the guest reached
  // checkout.
  const now = params.now ?? new Date()
  const clock = bookingClockNow(now)
  if (params.date < clock.date) return null

  const currentMinuteHasStarted = now.getSeconds() > 0 || now.getMilliseconds() > 0
  const nowFloor =
    params.date === clock.date
      ? currentMinuteHasStarted
        ? firstSlotAfter(clock.time)
        : firstSlotOnOrAfter(clock.time)
      : (TIME_SLOTS[0] ?? '00:00')
  if (!nowFloor) return null

  const bound = laterHHmm(nowFloor, params.changeoverMin)
  if (!bound) return null

  let candidate = firstSlotOnOrAfter(bound)
  while (
    candidate &&
    startsTooSoonForHostApproval(toBookingDateTimeISO(params.date, candidate), now.getTime())
  ) {
    candidate = firstSlotAfter(candidate)
  }
  return candidate
}

export function firstBookableStart(params: {
  unavailableDateSet: Set<string>
  earliestPickupByDate: Map<string, string>
  now?: Date
  maxDaysAhead?: number
}): { date: string; time: string } | null {
  const maxDaysAhead = params.maxDaysAhead ?? 540
  let date = bookingClockNow(params.now).date
  for (let offset = 0; offset <= maxDaysAhead; offset += 1) {
    if (!params.unavailableDateSet.has(date)) {
      const time = earliestPickupTimeOnDate({
        date,
        now: params.now,
        changeoverMin: params.earliestPickupByDate.get(date),
      })
      if (time) return { date, time }
    }
    date = addBookingDaysISO(date, 1)
  }
  return null
}
